import { useState, useEffect } from 'react';
import { SupabaseSong } from '../types/supabase';
import { spotifyService } from '../services/spotify';
import { supabase } from '../services/supabase';
import { AlbumArtwork } from './AlbumArtwork';
import spotifyIcon from '../assets/icons/spotify.svg';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  songs: SupabaseSong[];
  playlistId: string;
  onVote: (song: SupabaseSong, vote: number) => void;
}

export default function SearchModal({
  isOpen,
  onClose,
  songs,
  playlistId,
  onVote,
}: SearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSongs, setFilteredSongs] = useState<SupabaseSong[]>([]);
  const [spotifyResults, setSpotifyResults] = useState<SupabaseSong[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredSongs([]);
      setSpotifyResults([]);
      return;
    }

    // First search the current playlist
    const filtered = songs.filter(
      (song) =>
        song.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        song.artist.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    setFilteredSongs(filtered);

    // Then search Spotify
    const searchSpotify = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const results = await spotifyService.search(searchQuery);
        // Filter out songs already in the playlist from sportify results
        const playlistSongIds = new Set(
          songs
            .filter((song) => song.playlist_id === playlistId)
            .map((song) => String(song.spotify_id)),
        );
        const filteredResults = results.filter((song) => !playlistSongIds.has(String(song.id)));
        setSpotifyResults(filteredResults);
      } catch (err) {
        setError('Failed to search Spotify. Please try again.');
        console.error('Spotify search error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce the Spotify search
    const timeoutId = setTimeout(searchSpotify, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, songs, playlistId]);

  const handleAddSong = async (song: SupabaseSong) => {
    try {
      // Generate UUIDs for both the song and added_by fields
      const songId = crypto.randomUUID();
      const addedBy = crypto.randomUUID();
      const spotify_id = song.id; // Store the Spotify track ID explicitly
      const supabaseSong = {
        ...song,
        id: songId, // Override the Spotify track ID with our own UUID
        spotify_id, // Store the Spotify track ID explicitly
        playlist_id: playlistId,
        added_by: addedBy,
        created_at: new Date().toISOString(),
      };

      const { error } = await supabase.from('songs').insert(supabaseSong);

      if (error) throw error;

      // Automatically upvote the song for the user who added it
      onVote(supabaseSong, 1);

      // Close the modal after successful addition
      onClose();
      setSearchQuery('');
      setFilteredSongs([]);
      setSpotifyResults([]);
    } catch (err) {
      setError('Failed to add song to playlist. Please try again.');
      console.error('Error adding song:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-white p-6 dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Search Songs</h2>
          <button
            onClick={() => {
              onClose();
              setSearchQuery('');
              setFilteredSongs([]);
              setSpotifyResults([]);
            }}
            type="button"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by title or artist..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded border bg-white p-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-primary"
            autoFocus
          />
        </div>

        {error && <div className="mb-4 rounded bg-red-100 p-2 text-red-700">{error}</div>}

        <div className="mt-4 max-h-96 overflow-y-auto">
          {!searchQuery ? (
            <p className="py-4 text-center text-gray-500 dark:text-gray-400">
              Type to search for songs
            </p>
          ) : (
            <>
              {/* Playlist Results */}
              {filteredSongs.length > 0 && (
                <div className="mb-6">
                  <h3 className="mb-2 text-lg font-semibold">In Playlist</h3>
                  <div className="space-y-2">
                    {filteredSongs.map((song) => (
                      <div
                        key={song.id}
                        className="flex items-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <div className="mr-3 h-12 w-12 flex-shrink-0 overflow-hidden rounded">
                          <AlbumArtwork
                            src={song.album_artwork}
                            alt={`${song.title} album art`}
                            className="h-full w-full"
                          />
                        </div>
                        <div className="flex-grow">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {song.title}
                          </div>
                          <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                            {song.artist}
                          </div>
                          {song.spotify_id && (
                            <div className="mt-0.5 h-[15px]">
                              <img
                                src={spotifyIcon}
                                alt="Spotify"
                                className="h-full w-auto text-gray-500 dark:text-gray-400"
                              />
                            </div>
                          )}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {song.votes || 0} votes
                        </div>
                        <button
                          className="ml-2 rounded bg-green-500 px-2 py-1 text-white hover:bg-green-600"
                          onClick={() => onVote(song, 1)} // Upvote
                        >
                          Upvote
                        </button>
                        <button
                          className="ml-2 rounded bg-red-500 px-2 py-1 text-white hover:bg-red-600"
                          onClick={() => onVote(song, -1)} // Downvote
                        >
                          Downvote
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Spotify Results */}
              <div>
                <h3 className="mb-2 text-lg font-semibold">Spotify Results</h3>
                {isLoading ? (
                  <div className="py-4 text-center">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
                  </div>
                ) : spotifyResults.length > 0 ? (
                  <div className="space-y-2">
                    {spotifyResults.map((song) => (
                      <div
                        key={song.id}
                        className="flex items-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <div className="mr-3 h-12 w-12 flex-shrink-0 overflow-hidden rounded">
                          <AlbumArtwork
                            src={song.album_artwork}
                            alt={`${song.title} album art`}
                            className="h-full w-full"
                          />
                        </div>
                        <div className="flex-grow">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {song.title}
                          </div>
                          <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                            {song.artist}
                          </div>
                          <div className="mt-0.5 h-[15px]">
                            <img
                              src={spotifyIcon}
                              alt="Spotify"
                              className="h-full w-auto text-gray-500 dark:text-gray-400"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddSong(song)}
                          className="rounded bg-primary px-3 py-1 text-white transition-colors hover:bg-secondary"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-4 text-center text-gray-500 dark:text-gray-400">
                    No songs found
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
